import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import type { Transport } from "@tdelivr/api-client";
import { services } from "@tdelivr/api-client";
// The row shapes DRV-05 and DRV-01 publish, imported rather than restated. A column here that a
// service stopped returning is a build failure, not a blank cell — and these two are the whole of
// this screen's claim that the roster's halves can be joined at all.
import type { DriverProfile } from "@tdelivr/api-client/generated/driver/driver-profile.js";

import {
  boundedNote,
  driverName,
  initialsOf,
  presenceFor,
  presenceReason,
  reportedAgeText,
  rosterCounts,
  rosterPage,
  unnamedNote,
  PRESENCE_LOOKS,
  type RosterOrder,
  rosterDetail,
  type RosterRow,
} from "../driver-roster.ts";
import { stateForError, stateForList, type ViewState } from "../view-state.ts";
import { PageHeading } from "./PageHeading.tsx";
import { RecordDetail } from "./RecordDetail.tsx";
import { StateBadge } from "./StateBadge.tsx";
import { StateView } from "./StateView.tsx";

/**
 * The driver roster: who is online, where they are in their shift, and what they are on.
 *
 * **Two services, two halves, one key — and the key is the reason this page could not be built
 * before.** DRV-05 `GET /v1/driver-availability` answers presence, availability, `fresh` and
 * `dispatchable`; DRV-01 `GET /v1/driver-profiles` answers `displayName`. Until Document 07 §14 was
 * applied, DRV-01 keyed on `profile_id` and published the driver as `id` while every sibling
 * published `driverId`, so joining the two was asserting a relationship no contract stated — and
 * the failure mode is the worst available: one driver's name against another driver's row. Both
 * schemas now publish `driverId`, required, and `tests/contract/driver-identifier.test.ts` holds it
 * for the whole domain. The join is DRV-05's spine with DRV-01 as a lookup beside it, which is the
 * same shape the offer monitor already uses and is not the three-source merge R-05 refuses.
 *
 * **The design for this page is a 320px panel inside another screen.** `05 Dispatch Live Map.dc.html`
 * draws the only roster in the bundle: a header with a count, three stat tiles, and rows of avatar,
 * name, badge and age. No file in `GUI_Design/design/` draws a full-width Driver roster page,
 * although `02 Console Shell v3` and `04` both carry it in the rail. So every value here — the
 * tiles' `8px 10px` on `--sunken` at 10px radius, the 30px avatar, its dashed `#98A2B3` ring on a
 * row in doubt, the 13px/600 name, the mono 11.5px age and its `--s-warning-fg` — is that panel's,
 * read out of its inline styles; and the page chrome around it is this console's own, which is
 * `19 WP18 Dispatch Operations`'s data grid as the demand queue and the offer monitor already
 * built it. Recorded as Q-39, because composing a page the design does not draw is a decision
 * somebody should see rather than discover.
 *
 * **Nothing on this page is a command.** DRV-05's three writes are the driver's own app going on
 * shift, and the design's primary action here is "Message driver", which G-18 says has no service
 * to call. So the button is drawn and refused, with the reason on it — the treatment R-08 gave the
 * demand queue's `+ Manual assign` and R-09 gave the live map's seven. Every operator on this page
 * is view-only by construction rather than by role.
 */

const availabilityApi = services.driver__driver_availability;
const profilesApi = services.driver__driver_profile;

/**
 * How many rows to take, from each service.
 *
 * 200 is the maximum `limit` both contracts name, in the same words: "capped at 200 — more than
 * this board shows". A tenant with more than 200 drivers online at once is in a state this screen
 * cannot help with, and the honest thing then is to say the page is partial.
 */
const PER_FETCH = "200";

/** The design's own rows-per-page choices, in its order. */
const PER_PAGE_CHOICES = [5, 10, 25, 50] as const;

/** The design's sort options, in its order and its words. */
const ORDER_CHOICES: readonly { readonly value: RosterOrder; readonly label: string }[] = [
  { value: "default", label: "Default order" },
  { value: "state", label: "State" },
  { value: "az", label: "Name A–Z" },
  { value: "za", label: "Name Z–A" },
];

/**
 * DRV-05's `presence` filter, which has no unfiltered value.
 *
 * Two options rather than three, and that is the contract rather than an omission. The parameter is
 * a two-value enum and omitting it means `online`, so there is no request that answers both — and
 * offering "All presences" would be the trap the offer monitor's state filter avoids: a word that
 * promises a set no page can return. The default stays the service's own, and the board's closing
 * line says so in as many words.
 */
const PRESENCE_CHOICES = [
  { value: "online", label: "Online" },
  { value: "offline", label: "Offline" },
] as const;

/** DRV-05's `availability` filter, which does have an unfiltered value. */
const AVAILABILITY_CHOICES = [
  { value: "available", label: "available" },
  { value: "unavailable", label: "unavailable" },
] as const;

/** The export formats the design's menu offers, in its order and its words. */
const EXPORTS = [
  { format: "csv", glyph: "description", label: "CSV" },
  { format: "xls", glyph: "table_view", label: "Excel (.xls)" },
  { format: "pdf", glyph: "picture_as_pdf", label: "PDF" },
] as const;

type ExportFormat = (typeof EXPORTS)[number]["format"];

export interface DriverRosterProps {
  readonly transport: Transport;
  /** Innermost last: tenant, section, page. Rendered in the page body, never in the top bar. */
  readonly crumbs?: readonly string[];
  /** Injected so a lapsed lease can be exercised without waiting for one to run out. */
  readonly now?: () => number;
}

interface Loaded {
  readonly rows: readonly RosterRow[];
  /** DRV-05 answered a cursor, so this is the head of the list rather than all of it. */
  readonly truncated: boolean;
}

export function DriverRoster({ transport, crumbs, now }: DriverRosterProps): ReactNode {
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [names, setNames] = useState<ReadonlyMap<string, string>>(new Map());
  const [namesTruncated, setNamesTruncated] = useState(false);
  const [search, setSearch] = useState("");
  const [presence, setPresence] = useState<"online" | "offline" | undefined>(undefined);
  const [availability, setAvailability] = useState<"available" | "unavailable" | undefined>(
    undefined,
  );
  const [order, setOrder] = useState<RosterOrder>("default");
  const [page, setPage] = useState(1);
  const [selectedDriverId, setSelectedDriverId] = useState<string | undefined>(undefined);
  const [perPage, setPerPage] = useState<number>(10);
  const [exportOpen, setExportOpen] = useState(false);
  const exportRegion = useRef<HTMLSpanElement>(null);

  const load = useCallback(async () => {
    const client = availabilityApi.createDriverAvailabilityClient(transport);
    try {
      const answered = await client.getDriverAvailability(
        presence,
        availability,
        PER_FETCH,
        undefined,
      );
      setLoaded({
        rows: answered.drivers,
        truncated: typeof answered.nextAfter === "string" && answered.nextAfter.length > 0,
      });
      setFailure(undefined);
    } catch (error) {
      // One state rather than a banner over a half-rendered table. A roster showing four of forty
      // drivers with no explanation is worse than one showing none.
      setFailure(
        stateForError(error, ["driver-availability:read", "driver-availability:workload"]),
      );
      setLoaded(undefined);
    }
  }, [transport, presence, availability]);

  /*
   * The names, separately and optionally.
   *
   * `navigation.ts` grants this page both pairs of scopes, so unlike the offer monitor this read is
   * expected to succeed — but a refusal here must still not take the board down with it, because
   * the half that matters operationally is which drivers are online. A roster of identifiers is a
   * usable roster; an error page is not.
   */
  const loadNames = useCallback(async () => {
    try {
      const answered = await profilesApi
        .createDriverProfileClient(transport)
        .listDriverProfiles(undefined, PER_FETCH);
      setNames(
        new Map(
          (answered.driverProfiles as readonly DriverProfile[]).map((profile) => [
            profile.driverId,
            profile.displayName,
          ]),
        ),
      );
      setNamesTruncated(typeof answered.nextAfter === "string" && answered.nextAfter.length > 0);
    } catch {
      setNames(new Map());
      setNamesTruncated(false);
    }
  }, [transport]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadNames();
  }, [loadNames]);

  // The export menu closes on Escape and on a click outside, which the design file never needed to
  // handle because it is a reference rather than something an operator uses for a whole shift.
  useEffect(() => {
    if (!exportOpen) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setExportOpen(false);
    };
    const onPointer = (event: MouseEvent): void => {
      const region = exportRegion.current;
      if (region !== null && !region.contains(event.target as Node)) setExportOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return (): void => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [exportOpen]);

  const instant = now === undefined ? Date.now() : now();

  const view = useMemo(
    () => rosterPage(loaded?.rows ?? [], { search, order, page, perPage, now: instant, names }),
    [loaded, search, order, page, perPage, instant, names],
  );

  const counts = useMemo(() => rosterCounts(loaded?.rows ?? []), [loaded]);

  /*
   * No page-level instant, so no stale banner over the board.
   *
   * The demand queue can say how far behind it is because DSP-05 publishes a projection watermark.
   * DRV-05 publishes `updatedAt` per row and nothing page-level, so this screen cannot say that the
   * board as a whole is a minute old — and faking it from the moment the browser asked would call
   * the data fresh whenever the request succeeded.
   *
   * What it can say, and does, is which *rows* are not to be trusted: that is `fresh`, and it is
   * the per-row stale state rather than the page's. R-18.
   */
  const state = failure ?? stateForList(loaded?.rows);

  const clearFilters = (): void => {
    setSearch("");
    setPresence(undefined);
    setAvailability(undefined);
    setPage(1);
  };

  const bounded = boundedNote(
    loaded?.rows.length,
    loaded?.truncated ?? false,
    presence,
    Number(PER_FETCH),
  );
  const unnamed = unnamedNote(loaded?.rows, names, namesTruncated);

  /*
   * The selected driver, and whether the panel is the overlay.
   *
   * Held by identifier rather than by index, because the roster re-sorts itself as drivers go
   * stale and a selection kept by position would follow the position rather than the person — on
   * a board whose whole job is telling an operator which driver is which.
   *
   * Unlike the exceptions board, nothing is selected until a row is clicked. That board opens on
   * its worst row because triage has an obvious first item; a roster does not, and opening one
   * driver's record unasked is a claim that they are the one to look at.
   */
  const selected =
    selectedDriverId === undefined
      ? undefined
      : view.rows.find((row) => row.driverId === selectedDriverId);

  /*
   * The heading row, hoisted because the loading state draws it too.
   *
   * In `02 Console Shell v3` the headings sit outside the state switch: the design shimmers the
   * rows beneath a heading row that never moves, so the board does not jump when the data lands.
   * One element rather than two copies of this grid, which would be free to disagree. Q-287.
   */
  const head = (
    <div className="roster__head">
      <span>Driver</span>
      {/* Not "Last fix", which is the design's own label for the same cell. That label is
          DRV-06's: it means the last position received, and this page reads no location and
          holds no scope for one. DRV-05 publishes `lastHeartbeatAt`, which is when the
          driver's app last reported, and the column says that. Q-39. */}
      <span>Last heartbeat</span>
      {/* The design's own label for DRV-05's answer, taken from the live map's driver card —
          where it reads `Availability · Not published · DRV-05 · contract gap G-13`. G-13 is
          closed, so this is that cell with a source behind it at last. */}
      <span className="roster__head--right">Availability</span>
    </div>
  );

  return (
    <section className="roster page">
      <PageHeading
        crumbs={crumbs ?? []}
        title="Driver roster"
        source="DRV-05 · DRV-01"
        description="Who is online, where they are, and what they are on."
        /*
         * The design's `rosterCount`, in the design's own words: `drivers.length + ' online'`.
         *
         * In the heading's aside rather than beside a panel title, which is where the live map's
         * `Live` pill went for the same reason — the page's own heading already says what this is,
         * and a second "Driver roster" above the grid would be the title twice.
         */
        aside={
          loaded === undefined ? undefined : (
            <span className="roster__count">{`${loaded.rows.length} online`}</span>
          )
        }
        action={
          <button
            type="button"
            className="roster__primary"
            /*
             * Drawn, and refused — the same treatment R-08 gave the demand queue's `+ Manual
             * assign` and R-09 gave the live map's seven card buttons.
             *
             * `SCREEN_SPEC.md`'s "Drivers on shift" names this page's primary action as **Message
             * driver**, over a form of Driver and Message. G-18 is open and undecided: there is no
             * masked call or SMS service on this platform and no operation anywhere takes a message
             * for a driver. Making it live would mean inventing both a payload and a service.
             */
            disabled
            title="No masked call or messaging service exists on this platform. See contract gap G-18."
          >
            <span aria-hidden="true" className="roster__primary-glyph">
              add
            </span>
            Message driver
          </button>
        }
      />

      {/*
        The design's three tiles, with its own counts and its own words.

        All three have a source, which is unusual on this console — the demand queue drew one of
        four and the offer monitor none of four. They are classifications of the rows this page
        holds rather than totals over the tenant, so when DRV-05 answers a cursor the closing line
        below says they are floors. R-04 is why that line exists: nothing on this platform publishes
        a total, so a tile is a count of what was fetched or it is a guess.

        No fourth tile for `offline`. The design draws three, and an offline count over a listing
        that answers online drivers by default would read nought on every page.
      */}
      <div className="roster__tiles">
        <div className="roster__tile">
          <span className="roster__tile-count">{counts.available}</span>
          <span className="roster__tile-label">available</span>
        </div>
        <div className="roster__tile">
          <span className="roster__tile-count">{counts.job}</span>
          <span className="roster__tile-label">on a job</span>
        </div>
        <div className="roster__tile">
          <span className="roster__tile-count">{counts.stale}</span>
          <span className="roster__tile-label">stale</span>
        </div>
      </div>

      {/*
        The grid and its panel in one positioned container, which is what docks the panel.

        `.detail` is docked two ways and both are written against `.board-split`: at 1240 and
        above `.board-split:has(> .detail)` makes it a 420px second column, and below 1240 the
        panel is `position: absolute; inset: 0 0 0 auto`, positioned against `.board-split`'s
        own `position: relative`. This board had no such wrapper, so an opened row drew a
        full-width block stacked under the table at 1440, and below 1240 an overlay anchored to
        the page rather than to the board. `DemandQueue.tsx` carries the measurements that found
        it: the first time any of these six panels was photographed, on 1 November.

        Which docking is right for these six at all is Q-297 — `19 WP18 Dispatch Operations` draws
        a centred modal where `04 Dispatch Exceptions Board` draws this column, and that is a
        decision rather than a defect. The wrapper is not that decision: it is what makes the
        build do what its own stylesheet already says.
      */}
      <div className="board-split">
        <div className="roster__panel">
          <div className="roster__toolbar">
            <span className="roster__search">
              <span aria-hidden="true" className="roster__search-glyph">
                search
              </span>
              <input
                id="driver-roster-search"
                type="search"
                aria-label="Search"
                placeholder="Search"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
              />
            </span>
            <select
              id="driver-roster-presence"
              aria-label="Filter by presence"
              value={presence ?? "online"}
              onChange={(event) => {
                setPresence(event.target.value as "online" | "offline");
                setPage(1);
              }}
            >
              {PRESENCE_CHOICES.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
            <select
              id="driver-roster-availability"
              aria-label="Filter by availability"
              value={availability ?? ""}
              onChange={(event) => {
                setAvailability(
                  event.target.value === ""
                    ? undefined
                    : (event.target.value as "available" | "unavailable"),
                );
                setPage(1);
              }}
            >
              <option value="">All availability</option>
              {AVAILABILITY_CHOICES.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
            <select
              id="driver-roster-order"
              aria-label="Sort"
              value={order}
              onChange={(event) => {
                setOrder(event.target.value as RosterOrder);
              }}
            >
              {ORDER_CHOICES.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
            <span className="roster__export" ref={exportRegion}>
              <button
                type="button"
                className="roster__export-button"
                aria-expanded={exportOpen}
                onClick={() => {
                  setExportOpen((open) => !open);
                }}
              >
                <span aria-hidden="true" className="roster__export-glyph">
                  download
                </span>
                Export
              </button>
              {exportOpen ? (
                <div className="roster__export-menu" role="menu">
                  {EXPORTS.map((choice) => (
                    <button
                      key={choice.format}
                      type="button"
                      role="menuitem"
                      className="roster__export-item"
                      onClick={() => {
                        setExportOpen(false);
                        exportRows(view.all, choice.format, instant, names);
                      }}
                    >
                      <span aria-hidden="true" className="roster__export-item-glyph">
                        {choice.glyph}
                      </span>
                      {choice.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </span>
          </div>

          <StateView
            state={state}
            purpose="Drivers appear here as soon as one goes on shift."
            onRetry={() => {
              void load();
            }}
            skeleton={{
              head,
              columns: 3,
              rowClassName: "roster__row",
              // Both strings come from the trail the shell already draws, so the
              // sentence can never disagree with the breadcrumb above it.
              page: crumbs?.at(-1) ?? "this page",
              tenant: crumbs?.[0] ?? "this tenant",
            }}
          >
            {head}
            <div className="roster__rows">
              {view.rows.map((row) => {
                const presenceKey = presenceFor(row);
                const look = PRESENCE_LOOKS[presenceKey];
                const name = driverName(row, names);
                const reason = presenceReason(row);
                return (
                  <div
                    key={row.driverId}
                    className="roster__row"
                    data-presence={presenceKey}
                    data-selected={row.driverId === selectedDriverId ? "true" : undefined}
                    role="button"
                    tabIndex={0}
                    aria-pressed={row.driverId === selectedDriverId}
                    aria-label={`Open ${name ?? row.driverId}`}
                    onClick={() => setSelectedDriverId(row.driverId)}
                    onKeyDown={(event) => {
                      // Enter and Space, because a role of button promises both.
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setSelectedDriverId(row.driverId);
                      }
                    }}
                  >
                    <span className="roster__driver">
                      {/*
                      The design's 30px avatar, and its dashed variant for a row in doubt.
                      Initials where DRV-01 answered a name; the design has no avatar for a driver
                      with no name, so the glyph it uses for a driver everywhere else stands in.
                    */}
                      <span aria-hidden="true" className="roster__avatar">
                        {name === undefined ? "person" : initialsOf(name)}
                      </span>
                      <span className="roster__identity">
                        <span className="roster__name">{name ?? row.driverId}</span>
                        {name === undefined ? null : (
                          <span className="roster__id">{row.driverId}</span>
                        )}
                      </span>
                    </span>
                    <span className="roster__age">{reportedAgeText(row, instant)}</span>
                    <span className="roster__state">
                      <StateBadge
                        code="DRV-05"
                        status={row.availability}
                        label={look.word}
                        /*
                         * The class is given rather than looked up, and it has to be: DRV-05 is not
                         * in the token system's lifecycle table, and the state a roster row is in is
                         * not any one of its published enums — it is `presence`, `fresh` and
                         * `dispatchable` together. `presenceFor` holds that rule and
                         * `PRESENCE_LOOKS` holds the design's words for its four answers.
                         */
                        classKey={look.classKey}
                        {...(reason === undefined ? {} : { title: reason })}
                      />
                    </span>
                  </div>
                );
              })}
            </div>
            {view.total === 0 ? (
              <div className="roster__no-match">
                <span aria-hidden="true" className="roster__no-match-glyph">
                  search_off
                </span>
                Nothing matches this search or filter.
                <button type="button" className="roster__clear" onClick={clearFilters}>
                  Clear filters
                </button>
              </div>
            ) : null}
            <div className="roster__pager">
              <span>
                {view.total === 0
                  ? "No matching rows"
                  : `Showing ${(page - 1) * perPage + 1}–${Math.min(page * perPage, view.total)} of ${view.total}`}
              </span>
              <span className="roster__pager-controls">
                <select
                  id="driver-roster-per-page"
                  aria-label="Rows per page"
                  value={perPage}
                  onChange={(event) => {
                    setPerPage(Number(event.target.value));
                    setPage(1);
                  }}
                >
                  {PER_PAGE_CHOICES.map((choice) => (
                    <option key={choice} value={choice}>
                      {choice} per page
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  aria-label="Previous page"
                  disabled={page <= 1}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                  <span aria-hidden="true">chevron_left</span>
                </button>
                <span className="roster__page">
                  Page {Math.min(page, view.pageCount)} of {view.pageCount}
                </span>
                <button
                  type="button"
                  aria-label="Next page"
                  disabled={page >= view.pageCount}
                  onClick={() => setPage((current) => Math.min(view.pageCount, current + 1))}
                >
                  <span aria-hidden="true">chevron_right</span>
                </button>
              </span>
            </div>
          </StateView>

          {/* What this board actually fetched, said rather than hidden. Two lines at most: what the
            page covers, and how much of it has a name. */}
          {bounded === undefined ? null : (
            <p className="roster__bounded" role="status">
              {bounded}
            </p>
          )}
          {unnamed === undefined ? null : (
            <p className="roster__bounded" role="status">
              {unnamed}
            </p>
          )}
        </div>

        {/*
        The selected driver, beside the roster at 1240 and over it below — the arrangement the
        exceptions board already uses, and the same `.detail` component, so the two cannot drift.
        Nothing is drawn until a row is chosen: this board has no obvious first row to open.
      */}
        {selected === undefined ? null : (
          <RecordDetail
            record={rosterDetail(selected, names, instant)}
            open
            onClose={() => setSelectedDriverId(undefined)}
          />
        )}
      </div>

      {/*
        The closing note, in the design's own words.

        `05 Dispatch Live Map.dc.html`'s annotations state it twice: "Stale drivers are excluded from
        new offers until a fresh fix arrives", and the sentence that makes this board worth watching
        — a row still saying online whose lease lapsed is a driver whose app has gone silent. The
        second is DRV-05's own listing summary word for word, so it is the one quoted: it is the
        only sentence about this screen that both the design and the contract agree on.
      */}
      <div className="roster__note">
        <span aria-hidden="true" className="roster__note-glyph">
          info
        </span>
        <span>
          A row still saying online whose lease lapsed is a driver whose app stopped reporting — an
          offer sent to them goes into silence. Those rows sort to the top.
        </span>
      </div>
    </section>
  );
}

/**
 * Export what the operator is looking at, in the columns they are looking at.
 *
 * The filtered and ordered set rather than the page, and the words on screen rather than the raw
 * enums — so an exported row says the same thing the board did, including that a driver's state is
 * three published fields read together. Excel takes an HTML table, which is what
 * `application/vnd.ms-excel` has always meant in practice, and PDF is the browser's own print
 * dialogue. The same three formats design rule 4 asks of every grid.
 */
function exportRows(
  rows: readonly RosterRow[],
  format: ExportFormat,
  now: number,
  names: ReadonlyMap<string, string>,
): void {
  const header = ["Driver", "Driver ID", "Last heartbeat", "Availability"];
  const body = rows.map((row) => [
    driverName(row, names) ?? "",
    row.driverId,
    reportedAgeText(row, now),
    PRESENCE_LOOKS[presenceFor(row)].word,
  ]);

  if (format === "csv") {
    const quote = (value: string): string => `"${value.replaceAll('"', '""')}"`;
    const csv = [header, ...body].map((line) => line.map(quote).join(",")).join("\n");
    download(new Blob([csv], { type: "text/csv" }), "driver-roster.csv");
    return;
  }

  const table =
    '<table border="1" cellpadding="6" style="border-collapse:collapse;font-family:sans-serif;font-size:12px">' +
    `<tr>${header.map((cell) => `<th>${escapeHtml(cell)}</th>`).join("")}</tr>` +
    body
      .map((line) => `<tr>${line.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`)
      .join("") +
    "</table>";

  if (format === "xls") {
    download(
      new Blob([`<html><head><meta charset="utf-8"></head><body>${table}</body></html>`], {
        type: "application/vnd.ms-excel",
      }),
      "driver-roster.xls",
    );
    return;
  }

  const printable = window.open("", "_blank");
  if (printable === null) return;
  printable.document.write(
    `<html><head><title>driver-roster</title></head><body><h2 style="font-family:sans-serif">driver-roster</h2>${table}</body></html>`,
  );
  printable.document.close();
  printable.focus();
  printable.print();
}

function escapeHtml(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

function download(blob: Blob, name: string): void {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = name;
  document.body.append(link);
  link.click();
  setTimeout(() => {
    URL.revokeObjectURL(link.href);
    link.remove();
  }, 500);
}
